import React from 'react';
import { useTranslation } from 'react-i18next';
import { createMessengerChannelAccount } from '../services/channelAccountService';
import styles from './ConnectorsPage.module.css';

interface AddMessengerAccountFormProps {
  canManage: boolean;
  onCreated: () => void;
  onCancel: () => void;
}

const emptyForm = {
  displayName: '',
  pageId: '',
  pageAccessToken: '',
  isDefault: false,
};

export const AddMessengerAccountForm: React.FC<AddMessengerAccountFormProps> = ({
  canManage,
  onCreated,
  onCancel,
}) => {
  const { t } = useTranslation(['companies', 'admin']);
  const [form, setForm] = React.useState(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const save = async () => {
    if (!form.displayName.trim() || !form.pageId.trim() || !form.pageAccessToken.trim()) {
      setError(t('channelAccountAddRequiredFields', { ns: 'companies' }));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await createMessengerChannelAccount({
        displayName: form.displayName.trim(),
        pageId: form.pageId.trim(),
        pageAccessToken: form.pageAccessToken.trim(),
        isDefault: form.isDefault,
      });
      setForm(emptyForm);
      onCreated();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToCreateChannelAccount', { ns: 'companies' }));
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return null;
  }

  return (
    <div className={styles.channelAccountAddForm}>
      <h4>{t('channelAccountAddMessengerHeading', { ns: 'companies' })}</h4>
      <p style={{ color: '#516173', marginTop: 0 }}>
        {t('channelAccountAddMessengerHelp', { ns: 'companies' })}
      </p>

      {error ? <div className="error">{error}</div> : null}

      <div className="company-form-grid">
        <label>
          {t('channelAccountDisplayName', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.displayName}
            onChange={event => setForm(prev => ({ ...prev, displayName: event.target.value }))}
            placeholder={t('channelAccountDisplayNamePlaceholder', { ns: 'companies' })}
          />
        </label>

        <label>
          {t('messengerPageId', { ns: 'companies' })}
          <input
            className="text-input"
            value={form.pageId}
            onChange={event => setForm(prev => ({ ...prev, pageId: event.target.value }))}
          />
        </label>

        <label>
          {t('messengerPageAccessToken', { ns: 'companies' })}
          <input
            className="text-input"
            type="password"
            value={form.pageAccessToken}
            onChange={event => setForm(prev => ({ ...prev, pageAccessToken: event.target.value }))}
            autoComplete="new-password"
          />
        </label>

        <label className={styles.channelAccountCheckbox}>
          <input
            type="checkbox"
            checked={form.isDefault}
            onChange={event => setForm(prev => ({ ...prev, isDefault: event.target.checked }))}
          />
          {t('channelAccountDefaultLabel', { ns: 'companies' })}
        </label>
      </div>

      <div className="company-actions-inline" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-primary" onClick={() => { void save(); }} disabled={saving}>
          {saving ? t('savingEllipsis', { ns: 'admin' }) : t('channelAccountCreate', { ns: 'companies' })}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          {t('channelAccountCancelAdd', { ns: 'companies' })}
        </button>
      </div>
    </div>
  );
};
